import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Input, Button, Badge } from '@img/ui';
import { useSession } from '../lib/auth-client';
import { api, ApiError, type ApiSuccess } from '../lib/api';
import { SEO } from '../components/SEO';

interface MyProperty {
  id: string;
  title: string;
  type: string;
  purpose: string;
  price: number | string;
  area: number;
  city: string;
  district: string | null;
  status: string;
  coverImage: string | null;
}

export function RequestAuctionPage() {
  const { t: _t } = useTranslation();
  const { data: session } = useSession();
  const u = session?.user as { name?: string; phone?: string | null; email?: string | null } | undefined;

  // 🎯 خيار: مزاد على عقار موجود (existing) أو على عقار جديد لم يضفه (new)
  const [mode, setMode] = useState<'pick' | 'existing' | 'new'>('pick');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);

  // 🏘️ جلب عقارات المستخدم لو مسجّل دخول
  const { data: myProperties } = useQuery({
    queryKey: ['me', 'properties', 'for-auction'],
    queryFn: () => api.get<ApiSuccess<MyProperty[]>>('/api/me/properties').catch(() => ({ data: [] as MyProperty[] })),
    enabled: !!session?.user,
  });
  // فقط العقارات النشطة أو قيد المراجعة (مو المباعة/المؤجّرة) — هاذي اللي ينفع نقيم عليها مزاد
  const eligibleProperties = (myProperties?.data ?? []).filter((p) =>
    ['ACTIVE', 'PENDING_REVIEW', 'PENDING', 'DRAFT'].includes(p.status),
  );

  const [form, setForm] = useState({
    name:          u?.name ?? '',
    phone:         u?.phone ?? '',
    email:         u?.email ?? '',
    propertyId:    '',          // عند اختيار عقار موجود
    propertyTitle: '',
    propertyType:  '',
    city:          '',
    district:      '',
    area:          '',
    expectedPrice: '',
    description:   '',
    notes:         '',
  });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await api.post('/api/auction-requests', {
        ...form,
        area: form.area ? Number(form.area) : null,
        expectedPrice: form.expectedPrice ? Number(form.expectedPrice) : null,
      });
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) {
        const fe: Record<string, string> = {};
        Object.entries(err.fieldErrors).forEach(([k, v]) => { fe[k] = v[0] ?? ''; });
        setErrors(fe);
      } else {
        setErrors({ _: (err as Error).message });
      }
    } finally {
      setBusy(false);
    }
  };

  // 🎯 ملء التفاصيل تلقائياً من العقار المختار
  const pickExistingProperty = (p: MyProperty) => {
    setSelectedPropertyId(p.id);
    setForm((f) => ({
      ...f,
      propertyId: p.id,
      propertyTitle: p.title,
      propertyType: p.type,
      city: p.city,
      district: p.district ?? '',
      area: String(p.area),
      expectedPrice: String(p.price),
    }));
    setMode('existing');
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <SEO
        title="طلب إقامة مزاد عقاري على عقارك 🔨"
        description="اطلب إقامة مزاد عقاري مباشر على عقارك مع شركة صورة العقارية. مزادات حية موثّقة، محرّجون معتمدون، نظام بثّ مباشر بالصوت والصورة، وتوثيق رقمي كامل. الهيئة العامة للعقار (REGA) معتمدة."
        keywords="طلب إقامة مزاد, مزاد على عقاري, بيع بالمزاد العلني, مزاد عقاري السعودية, مزاد فلل, مزاد أراضي, مزاد عمائر, مزاد محلات تجارية, مزاد مزارع, مزاد استراحات, محرّج عقاري معتمد, مزاد إلكتروني مباشر, مزاد على الهواء, خدمة مزاد عقاري, شركة مزادات سعودية, مزادات صورة, البورصة العقارية, REGA, img.sa"
        canonical="https://img.sa/request-auction"
        jsonLd={{
          '@type': 'Service',
          name: 'خدمة إقامة المزادات العقارية',
          provider: { '@type': 'Organization', name: 'شركة صورة العقارية', url: 'https://img.sa' },
          serviceType: 'Real Estate Auction',
          areaServed: { '@type': 'Country', name: 'SA' },
          description: 'خدمة إقامة مزادات عقارية مباشرة موثّقة بمحرّجين معتمدين ونظام بثّ بالصوت والصورة.',
          offers: { '@type': 'Offer', priceCurrency: 'SAR', url: 'https://img.sa/request-auction' },
        }}
      />
      <div className="text-center mb-8">
        <div className="text-5xl mb-3">🔨</div>
        <h1 className="text-3xl font-black text-navy-dark mb-2">طلب إقامة مزاد على عقارك</h1>
        <p className="text-gray-600">اطلب إقامة مزاد، وسيراجع فريقنا الطلب ويحدّد لك موعد المزاد المناسب</p>
      </div>

      {/* 🎯 شاشة اختيار: عقار موجود ولا جديد؟ — تظهر للمستخدم المسجّل */}
      {!success && mode === 'pick' && session?.user && (
        <Card className="mb-6">
          <CardBody className="p-6">
            <h2 className="text-lg font-black text-navy-dark mb-1">📋 على أي عقار تبي تقيم المزاد؟</h2>
            <p className="text-xs text-ink-muted mb-5">اختر من عقاراتك المضافة، أو أضف عقاراً جديداً ضمن طلب المزاد.</p>

            {eligibleProperties.length > 0 && (
              <>
                <div className="text-xs font-black text-navy-dark mb-2">🏘️ عقاراتي ({eligibleProperties.length})</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                  {eligibleProperties.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => pickExistingProperty(p)}
                      className="text-right p-3 rounded-xl border-2 border-gray-200 hover:border-navy hover:bg-navy-soft transition flex gap-3 items-start"
                    >
                      {p.coverImage ? (
                        <img src={p.coverImage} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-navy-soft flex items-center justify-center text-2xl shrink-0">🏠</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 flex-wrap mb-1">
                          <Badge color={p.status === 'ACTIVE' ? 'green' : 'amber'}>
                            {p.status === 'ACTIVE' ? 'منشور' : 'قيد المراجعة'}
                          </Badge>
                          <span className="text-[10px] text-ink-muted">{p.type}</span>
                        </div>
                        <h4 className="font-bold text-sm text-navy-dark line-clamp-1">{p.title}</h4>
                        <div className="text-[11px] text-ink-muted">
                          📍 {p.city}{p.district ? ` · ${p.district}` : ''} · 📐 {p.area} م²
                        </div>
                        <div className="text-xs font-black text-gold-dark mt-1">
                          {Number(p.price).toLocaleString('en-US')} ر.س
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* خيار: أضف عقاراً جديداً */}
            <button
              onClick={() => setMode('new')}
              className="w-full p-4 rounded-2xl border-2 border-dashed border-gold/40 hover:border-gold hover:bg-gold-soft transition flex items-center gap-3"
            >
              <div className="w-12 h-12 rounded-xl bg-gold-soft text-gold-dark flex items-center justify-center text-xl shrink-0">➕</div>
              <div className="flex-1 text-right">
                <div className="font-black text-navy-dark">عقار جديد لم أضفه بعد</div>
                <div className="text-xs text-ink-muted">سأدخل تفاصيله ضمن طلب المزاد — وسيُسجَّل في حسابي</div>
              </div>
            </button>

            {eligibleProperties.length === 0 && session?.user && (
              <div className="bg-info-bg border border-info/30 rounded-xl p-3 text-xs text-info-dark mt-3">
                💡 لم تُضِف أي عقار بعد. يمكنك:
                {' '}
                <Link to="/submit-property" className="font-bold underline">إضافة العقار أولاً</Link>
                {' '}
                ثم العودة لطلب المزاد، أو تابع هنا لإدخال تفاصيله مباشرة.
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* النموذج يظهر فقط بعد اختيار وضع (موجود أو جديد) — أو لو الضيف */}
      {(mode !== 'pick' || !session?.user) && (
      <Card>
        <CardBody className="p-8">
          {/* 🔙 زر الرجوع لاختيار العقار */}
          {mode !== 'pick' && session?.user && (
            <button
              onClick={() => { setMode('pick'); setSelectedPropertyId(null); }}
              className="text-xs text-navy-dark hover:underline mb-4 font-bold"
            >
              ← تغيير العقار
            </button>
          )}
          {/* 🏷️ شارة توضّح النوع */}
          {mode === 'existing' && selectedPropertyId && (
            <div className="bg-navy-soft border border-navy/20 rounded-xl p-3 text-xs text-navy-dark mb-5">
              ✓ <b>المزاد على عقار موجود</b> — تفاصيل العقار مأخوذة من حسابك. أكمل بياناتك أدناه.
            </div>
          )}
          {mode === 'new' && (
            <div className="bg-gold-soft border border-gold/40 rounded-xl p-3 text-xs text-gold-dark mb-5">
              ➕ <b>عقار جديد</b> — أدخل تفاصيل العقار، وسيُسجَّل تلقائياً في حسابك بعد قبول طلب المزاد.
            </div>
          )}
          {success ? (
            <div className="text-center py-8">
              <div className="text-6xl mb-3">✅</div>
              <h2 className="text-2xl font-black text-navy-dark mb-2">تم استلام طلبك بنجاح!</h2>
              <p className="text-gray-600 mb-6">سيراجع فريقنا الطلب ويتواصل معك خلال ساعات لتحديد موعد المزاد.</p>
              <Button variant="primary" onClick={() => { setSuccess(false); setForm({ ...form, propertyTitle: '', propertyType: '', city: '', district: '', area: '', expectedPrice: '', description: '', notes: '' }); }}>
                طلب مزاد آخر
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <fieldset className="border border-gray-200 rounded-xl p-4">
                <legend className="px-2 font-black text-navy-dark text-sm">👤 معلوماتك</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="الاسم *"  value={form.name}  onChange={set('name')}  error={errors.name}  required />
                  <Input label="الجوال *" value={form.phone} onChange={set('phone')} error={errors.phone} required type="tel" dir="ltr" />
                  <div className="md:col-span-2">
                    <Input label="البريد الإلكتروني" value={form.email} onChange={set('email')} error={errors.email} type="email" />
                  </div>
                </div>
              </fieldset>

              <fieldset className="border border-gray-200 rounded-xl p-4">
                <legend className="px-2 font-black text-navy-dark text-sm">🏠 معلومات العقار</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="عنوان مختصر للعقار *" value={form.propertyTitle} onChange={set('propertyTitle')} error={errors.propertyTitle} required hint="مثل: فيلا في حي النرجس" />
                  <div>
                    <label className="block text-sm font-bold text-ink mb-1">نوع العقار</label>
                    <select value={form.propertyType} onChange={set('propertyType')} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                      <option value="">اختر…</option>
                      <option value="VILLA">فيلا</option>
                      <option value="APARTMENT">شقة</option>
                      <option value="LAND">أرض</option>
                      <option value="COMMERCIAL">تجاري</option>
                      <option value="OFFICE">مكتب</option>
                      <option value="BUILDING">عمارة</option>
                      <option value="FARM">مزرعة / استراحة</option>
                    </select>
                  </div>
                  <Input label="المدينة"   value={form.city}     onChange={set('city')} />
                  <Input label="الحي"      value={form.district} onChange={set('district')} />
                  <Input label="المساحة (م²)"      type="number" value={form.area}          onChange={set('area')} />
                  <Input label="السعر المتوقع (ر.س)" type="number" value={form.expectedPrice} onChange={set('expectedPrice')} hint="السعر اللي تتوقع تبيع به" />
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-ink mb-1">وصف العقار</label>
                    <textarea
                      value={form.description}
                      onChange={set('description')}
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
                      placeholder="مثلاً: عدد الغرف، التشطيب، المزايا، إلخ"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-ink mb-1">ملاحظات إضافية للمحرّج</label>
                    <textarea
                      value={form.notes}
                      onChange={set('notes')}
                      rows={2}
                      className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
                      placeholder="أي تفضيلات أو معلومات تحب فريقنا يعرفها"
                    />
                  </div>
                </div>
              </fieldset>

              {errors._ && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{errors._}</div>}

              <div className="flex justify-end">
                <Button type="submit" variant="gold" size="lg" loading={busy}>📨 إرسال الطلب</Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>
      )}
    </div>
  );
}
